/**
 * IconButton - Icon-only button
 *
 * Usage:
 * <IconButton icon={<PencilIcon />} label="Edit" />
 * <IconButton icon={<TrashIcon />} label="Delete" variant="danger" />
 * <IconButton icon={<MenuIcon />} label="Menu" size="lg" />
 */

import { forwardRef } from 'react';

import { Spinner } from '../feedback';
import { cn } from '../utils';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon to display */
  icon: React.ReactNode;
  /** Accessible label (required for a11y) */
  label: string;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Show loading state */
  loading?: boolean;
}

const variantStyles = {
  primary: 'bg-blue-600 text-white hover:bg-blue-500 focus-visible:ring-blue-500',
  secondary:
    'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:hover:text-white focus-visible:ring-gray-500 dark:focus-visible:ring-zinc-500',
  ghost:
    'bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-400 dark:hover:bg-white/[0.04] dark:hover:text-white focus-visible:ring-gray-500 dark:focus-visible:ring-zinc-500',
  danger:
    'bg-red-600/10 text-red-500 hover:bg-red-600/20 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 focus-visible:ring-red-500',
};

const sizeStyles = {
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-10 w-10',
};

const iconSizes = {
  sm: 'h-4 w-4',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      label,
      type = 'button',
      variant = 'ghost',
      size = 'md',
      loading,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-label={label}
        title={label}
        className={cn(
          'inline-flex items-center justify-center rounded-lg transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-[#09090b]',
          variantStyles[variant],
          sizeStyles[size],
          isDisabled && 'cursor-not-allowed opacity-50',
          className
        )}
        {...props}
      >
        {loading ? (
          <Spinner size="sm" className="text-current" />
        ) : (
          <span
            className={cn(
              'flex items-center justify-center [&>svg]:h-full [&>svg]:w-full',
              iconSizes[size]
            )}
          >
            {icon}
          </span>
        )}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
